import { buildRouteRegistryEntries } from './navigation-placeholder-routes';
import { PLACEHOLDER_SCREEN_DEFS } from './navigation-placeholder-manifest';

export interface RouteRegistryEntry {
  route: string;
  /** Routes with :id params are excluded from strict menu coverage checks */
  isParameterized?: boolean;
  excludeFromMenuCheck?: boolean;
}

/** Retired legacy experimental routes — excluded from strict menu coverage checks (Phase 11.7). */
const RETIRED_LEGACY_EXPERIMENTAL_APP_ROUTES: RouteRegistryEntry[] = [
  { route: '/financial/sales/invoices', excludeFromMenuCheck: true },
  { route: '/financial/sales/offline-commerce', excludeFromMenuCheck: true },
  { route: '/financial/sales/sales-review', excludeFromMenuCheck: true },
  { route: '/financial/sales/reports/summary', excludeFromMenuCheck: true },
  { route: '/financial/sales/reports/low-stock', excludeFromMenuCheck: true },
  { route: '/financial/sales/reports/high-sales', excludeFromMenuCheck: true },
  { route: '/financial/sales/reports/purchased-items', excludeFromMenuCheck: true },
  { route: '/financial/sales/reports/country-sales', excludeFromMenuCheck: true },
  { route: '/financial/sales/reports/customers', excludeFromMenuCheck: true },
  { route: '/financial/lending/configuration', excludeFromMenuCheck: true },
  { route: '/financial/lending/interest-categories', excludeFromMenuCheck: true },
  { route: '/financial/lending/suppliers', excludeFromMenuCheck: true },
  { route: '/financial/lending/investors', excludeFromMenuCheck: true },
  { route: '/financial/lending/customer-credit-profiles', excludeFromMenuCheck: true },
  { route: '/financial/lending/loans', excludeFromMenuCheck: true },
  { route: '/financial/lending/installments', excludeFromMenuCheck: true },
  { route: '/financial/lending/salary-deductions', excludeFromMenuCheck: true },
  { route: '/financial/lending/payroll-partnership', excludeFromMenuCheck: true },
  { route: '/financial/lending/send-deductions', excludeFromMenuCheck: true },
  { route: '/financial/lending/accounting-match', excludeFromMenuCheck: true },
  { route: '/financial/transactions/fin', excludeFromMenuCheck: true },
  { route: '/financial/transactions/lend', excludeFromMenuCheck: true },
  { route: '/financial/transactions/wallet', excludeFromMenuCheck: true },
  { route: '/financial/transactions/ai-fin-suite', excludeFromMenuCheck: true },
  { route: '/financial/assets/register', excludeFromMenuCheck: true }
];

export const REGISTERED_APP_ROUTES: RouteRegistryEntry[] = [
  { route: '/dashboard' },
  { route: '/login', excludeFromMenuCheck: true },

  { route: '/platform/tenants' },
  { route: '/platform/tenants/provision' },
  { route: '/platform/tenants/:id', isParameterized: true },
  { route: '/platform/localization-preferences' },
  { route: '/platform/localization-resources' },
  { route: '/platform/system-operations' },
  { route: '/platform/running-numbers' },
  { route: '/platform/parties' },
  { route: '/platform/parties/kinds/:kindKey', isParameterized: true },
  { route: '/platform/parties/:partyId', isParameterized: true },
  { route: '/platform/customer-groups' },
  { route: '/platform/customer-groups/:groupId', isParameterized: true },
  { route: '/platform/user-party-bindings' },
  { route: '/platform/user-party-bindings/:bindingId', isParameterized: true },
  { route: '/platform/compliance-queue/pending-kyc-kyb' },

  { route: '/identity/users' },
  { route: '/identity/roles' },
  { route: '/identity/roles/:roleId/permissions', isParameterized: true },
  { route: '/subscription/current' },

  { route: '/catalog' },
  { route: '/catalog/products' },
  { route: '/catalog/categories', excludeFromMenuCheck: true },
  { route: '/catalog/offerings', excludeFromMenuCheck: true },
  { route: '/catalog/attributes', excludeFromMenuCheck: true },
  { route: '/catalog/media', excludeFromMenuCheck: true },
  { route: '/catalog/offerings/:id', isParameterized: true },
  { route: '/catalog/offering-profiles', excludeFromMenuCheck: true },

  { route: '/sales/document-profiles' },
  { route: '/sales/document-profiles/:profileId', isParameterized: true },
  { route: '/sales/price-lists' },
  { route: '/sales/price-lists/:priceListId', isParameterized: true },
  { route: '/sales/campaigns' },
  { route: '/sales/campaigns/:campaignId', isParameterized: true },
  { route: '/sales/coupons' },
  { route: '/sales/coupons/:couponId', isParameterized: true },
  { route: '/sales/channels' },
  { route: '/sales/channels/:channelId', isParameterized: true },
  { route: '/sales/territories' },
  { route: '/sales/territories/:territoryId', isParameterized: true },
  { route: '/sales/quotas' },
  { route: '/sales/quotas/:quotaId', isParameterized: true },
  { route: '/sales/reservations' },
  { route: '/sales/reservations/:reservationId', isParameterized: true },
  { route: '/sales/allocation-policies' },
  { route: '/sales/allocation-policies/:policyId', isParameterized: true },
  { route: '/sales/credit-policies' },
  { route: '/sales/credit-policies/:policyId', isParameterized: true },
  { route: '/sales/customer-credit-limits' },
  { route: '/sales/customer-credit-limits/:limitId', isParameterized: true },
  { route: '/sales/credit-decisions' },
  { route: '/sales/installment-plans' },
  { route: '/sales/installment-plans/:planId', isParameterized: true },
  { route: '/sales/commission-plans' },
  { route: '/sales/commission-plans/:planId', isParameterized: true },
  { route: '/sales/quotes' },
  { route: '/sales/quotes/:orderId', isParameterized: true },
  { route: '/sales/contracts' },
  { route: '/sales/contracts/:contractId', isParameterized: true },
  { route: '/sales/tenders' },
  { route: '/sales/tenders/:tenderId', isParameterized: true },
  { route: '/sales/ai-recommendations' },
  { route: '/sales/ai-recommendations/:recommendationId', isParameterized: true },
  { route: '/sales/ai-models' },
  { route: '/sales/consignment-contracts' },
  { route: '/sales/consignment-contracts/:contractId', isParameterized: true },
  { route: '/sales/collection-cases' },
  { route: '/sales/collection-cases/:caseId', isParameterized: true },
  { route: '/sales/distribution-routes' },
  { route: '/sales/distribution-routes/:routeId', isParameterized: true },
  { route: '/sales/distribution-visits/:visitId', isParameterized: true },
  { route: '/sales/distribution-settlements' },
  { route: '/sales/orders' },
  { route: '/sales/orders/:orderId', isParameterized: true },

  { route: '/inventory/warehouses' },
  { route: '/inventory/warehouses/:id', isParameterized: true },
  { route: '/inventory/movements' },
  { route: '/inventory/movements/:id', isParameterized: true },
  { route: '/inventory/count-sessions' },
  { route: '/inventory/inbound-shipments' },
  { route: '/inventory/inbound-shipments/:id', isParameterized: true },
  { route: '/inventory/requisitions' },
  { route: '/inventory/requisitions/:id', isParameterized: true },
  { route: '/inventory/supply-orders/:id', isParameterized: true },
  { route: '/inventory/replenishment-policies' },
  { route: '/inventory/replenishment-policies/:id', isParameterized: true },
  { route: '/inventory/replenishment-breaches' },
  { route: '/inventory/expiry-status' },
  { route: '/inventory/reports' },
  { route: '/inventory/reservations' },
  { route: '/inventory/pick-waves' },
  { route: '/inventory/pick-waves/:id', isParameterized: true },
  { route: '/inventory/warehouse-tasks' },
  { route: '/inventory/outbound-shipments' },
  { route: '/inventory/carrier-webhook-events' },
  { route: '/inventory/operator' },
  { route: '/inventory/kpi-dashboard' },
  { route: '/inventory/internal-consumption' },
  { route: '/inventory/lots' },
  { route: '/inventory/lots/:id', isParameterized: true },
  { route: '/inventory/serials' },
  { route: '/inventory/serials/:id', isParameterized: true },
  { route: '/inventory/qc-inspections' },
  { route: '/inventory/qc-inspections/:id', isParameterized: true },
  { route: '/inventory/traceability' },
  { route: '/inventory/recalls' },
  { route: '/inventory/recalls/:id', isParameterized: true },
  { route: '/inventory/tracking-dashboard' },
  { route: '/inventory/costing/policies' },
  { route: '/inventory/costing/policies/:id', isParameterized: true },
  { route: '/inventory/costing/valuation' },
  { route: '/inventory/costing/valuation/snapshots/:id', isParameterized: true },
  { route: '/inventory/costing/layers' },
  { route: '/inventory/costing/layers/:id', isParameterized: true },
  { route: '/inventory/costing/revaluations' },
  { route: '/inventory/costing/revaluations/:id', isParameterized: true },
  { route: '/inventory/costing/operability' },

  { route: '/services/orders' },
  { route: '/services/orders/:id', isParameterized: true },
  { route: '/services/resources' },
  { route: '/services/rma' },
  { route: '/services/rma/:id', isParameterized: true },
  { route: '/services/dispatch-board' },
  { route: '/services/technician' },
  { route: '/operations/control-room' },

  { route: '/assets/register' },
  { route: '/assets/register/:id', isParameterized: true },
  { route: '/rental/agreements' },
  { route: '/rental/agreements/:id', isParameterized: true },
  { route: '/rental/booking-board' },
  { route: '/hospitality/properties' },
  { route: '/hospitality/properties/:id', isParameterized: true },
  { route: '/hospitality/reservations' },
  { route: '/hospitality/reservations/:id', isParameterized: true },
  { route: '/hospitality/housekeeping' },
  { route: '/hospitality/rate-calendar' },
  { route: '/hospitality/front-desk' },
  { route: '/hospitality/kpi-dashboard' },
  { route: '/hospitality/booking-board' },
  { route: '/hospitality/policy-exceptions' },
  { route: '/hospitality/room-maintenance' },
  { route: '/commerce/orders' },
  { route: '/regulated-finance/cockpit' },
  { route: '/digital/entitlements/:id' },
  { route: '/digital/entitlements' },
  { route: '/subscription/contracts' },
  { route: '/subscription/contracts/:id', isParameterized: true },
  { route: '/marketplace/dashboard' },
  { route: '/marketplace/launch-readiness' },
  { route: '/marketplace/global-commerce' },
  { route: '/marketplace/optimization' },
  { route: '/marketplace/sellers' },
  { route: '/marketplace/offering-proposals' },
  { route: '/marketplace/storefront/stores' },
  { route: '/marketplace/storefront/publishing' },
  { route: '/marketplace/storefront/category-navigation' },
  { route: '/marketplace/storefront/home-composer' },
  { route: '/marketplace/storefront/content-blocks' },
  { route: '/cmms/work-orders' },
  { route: '/cmms/work-orders/:id', isParameterized: true },
  { route: '/cmms/downtime' },
  { route: '/cmms/plans' },

  { route: '/workflow/definitions' },
  { route: '/workflow/definitions/:id', isParameterized: true },
  { route: '/workflow/intents' },
  { route: '/workflow/intents/new' },
  { route: '/workflow/intents/governance' },
  { route: '/workflow/intents/approvals/inbox' },
  { route: '/workflow/intents/:id', isParameterized: true },
  { route: '/workflow/intents/evaluation' },
  { route: '/workflow/intents/evaluation/simulation' },
  { route: '/workflow/intents/evaluation/runs/:runId', isParameterized: true },
  { route: '/workflow/instances' },
  { route: '/workflow/instances/:id', isParameterized: true },
  { route: '/workflow/triggers' },
  { route: '/workflow/hooks/bindings' },
  { route: '/workflow/tasks' },
  { route: '/workflow/tasks/:id', isParameterized: true },
  { route: '/workflow/forms' },
  { route: '/workflow/forms/:slug', isParameterized: true },

  { route: '/dynamic/records' },
  { route: '/dynamic/forms' },
  { route: '/dynamic/forms/:id', isParameterized: true },
  { route: '/dynamic/entities' },
  { route: '/dynamic/entities/:id', isParameterized: true },
  { route: '/dynamic/entities/:entityId/records', isParameterized: true },
  { route: '/dynamic/migrations' },
  { route: '/dynamic/migrations/:id', isParameterized: true },
  { route: '/dynamic/import-export' },
  { route: '/dynamic/audit' },
  { route: '/dynamic/analytics' },
  { route: '/dynamic/storefront-catalog' },

  { route: '/finance/overview' },
  // Launch-track payments (Phase 8–11) — active navigation; do not retire.
  { route: '/payments/providers' },
  { route: '/payments/intents' },
  { route: '/payments/wallet' },
  { route: '/payments/lending' },
  { route: '/payments/bnpl' },
  { route: '/payments/portfolio' },
  { route: '/payments/financial-completeness' },

  // Party Hub customers — launch track; not legacy treasury (Phase 11.7 exception).
  { route: '/financial/payments/customers' },
  { route: '/financial/payments/customers/:customerId', isParameterized: true },

  { route: '/financial/control-center' },
  { route: '/financial/budgets' },
  { route: '/financial/reconciliation' },
  { route: '/financial/consolidation' },
  { route: '/financial/semantic-metrics' },
  { route: '/financial/ai-recommendations' },

  { route: '/financial/payments/banks' },
  { route: '/financial/payments/banks/:bankId', isParameterized: true },
  { route: '/financial/payments/cashboxes' },
  { route: '/financial/payments/cashboxes/:cashboxId', isParameterized: true },
  { route: '/financial/payments/receipts' },
  { route: '/financial/payments/receipts/:receiptId', isParameterized: true },
  { route: '/financial/payments/payouts' },
  { route: '/financial/payments/payouts/:paymentRecordId', isParameterized: true },
  { route: '/financial/payments/daily-overview' },
  { route: '/financial/payments/reports/time-person-receipts' },
  { route: '/financial/payments/reports/time-person-payouts' },
  { route: '/financial/payments/reports/bank-cashbox' },

  { route: '/financial/management/chart-of-accounts' },
  { route: '/financial/management/journal-entry' },
  { route: '/financial/management/journal-entry/create', excludeFromMenuCheck: true },
  { route: '/financial/management/journal-entry/:journalId/edit', isParameterized: true, excludeFromMenuCheck: true },
  { route: '/financial/management/accounting-review' },
  { route: '/financial/management/closing' },
  { route: '/financial/management/closing/create', excludeFromMenuCheck: true },
  { route: '/financial/management/closing/:journalId/edit', isParameterized: true, excludeFromMenuCheck: true },
  { route: '/financial/management/opening' },
  { route: '/financial/management/opening/create', excludeFromMenuCheck: true },
  { route: '/financial/management/opening/:journalId/edit', isParameterized: true, excludeFromMenuCheck: true },

  { route: '/financial/assets/groups' },
  { route: '/financial/assets/depreciation' },
  { route: '/financial/assets/revaluation' },
  { route: '/financial/assets/ledger' },
  { route: '/financial/assets/settings' },

  { route: '/insurance' },
  { route: '/insurance/policies' },
  { route: '/insurance/products' },
  { route: '/insurance/product-versions' },
  { route: '/insurance/products/new', excludeFromMenuCheck: true },
  { route: '/insurance/products/:productId', isParameterized: true },
  { route: '/insurance/products/:productId/versions/new', isParameterized: true, excludeFromMenuCheck: true },
  { route: '/insurance/products/:productId/versions/:versionId', isParameterized: true },
  { route: '/insurance/rating' },
  { route: '/insurance/claims' },
  { route: '/insurance/producers' },
  { route: '/insurance/producers/new', excludeFromMenuCheck: true },
  { route: '/insurance/producers/:producerId', isParameterized: true },
  { route: '/insurance/distribution' },
  { route: '/insurance/commission-plans' },
  { route: '/insurance/commission-plans/new', excludeFromMenuCheck: true },
  { route: '/insurance/commission-plans/:planId', isParameterized: true },
  { route: '/insurance/commission-accruals' },
  { route: '/insurance/commission-settlements' },
  { route: '/insurance/commission-settlements/new', excludeFromMenuCheck: true },
  { route: '/insurance/commission-settlements/:settlementId', isParameterized: true },
  { route: '/insurance/premium-invoices' },
  { route: '/insurance/premium-invoices/:invoiceId', isParameterized: true },
  { route: '/insurance/claims/new', excludeFromMenuCheck: true },
  { route: '/insurance/claims/:claimId', isParameterized: true },
  { route: '/insurance/policies/new', excludeFromMenuCheck: true },
  { route: '/insurance/policies/:policyId', isParameterized: true },
  { route: '/insurance/ai-recommendations' },
  { route: '/insurance/ai-recommendations/:recommendationId', isParameterized: true },
  { route: '/insurance/quotes' },
  { route: '/insurance/quotes/:quoteId', isParameterized: true },
  { route: '/insurance/proposals' },
  { route: '/insurance/proposals/:proposalId', isParameterized: true },
  { route: '/insurance/uw-cases' },
  { route: '/insurance/uw-cases/:caseId', isParameterized: true },
  { route: '/insurance/master-policies' },
  { route: '/insurance/master-policies/:masterPolicyId', isParameterized: true },
  { route: '/insurance/reinsurance/treaties' },
  { route: '/insurance/reinsurance/treaties/:treatyId', isParameterized: true },
  { route: '/insurance/regulatory/jobs' },
  { route: '/insurance/health/providers' },
  { route: '/insurance/health/providers/:providerId', isParameterized: true },

  { route: '/event-hub/catalog' },
  { route: '/event-hub/catalog/:eventType/versions/:version', isParameterized: true },
  { route: '/event-hub/webhook-subscriptions' },
  { route: '/event-hub/webhook-subscriptions/:id', isParameterized: true },
  { route: '/event-hub/observability' },
  { route: '/knowledge/sources' },
  { route: '/knowledge/sources/:sourceId', isParameterized: true },
  { route: '/iot/devices' },
  { route: '/iot/devices/:deviceId', isParameterized: true },
  { route: '/growth/optimizer/guardrails', excludeFromMenuCheck: true },
  { route: '/growth/optimizer/automation', excludeFromMenuCheck: true },
  { route: '/growth/optimizer/runs/:runId', isParameterized: true, excludeFromMenuCheck: true },
  { route: '/growth/marketing/ace-studio' },
  { route: '/growth/marketing/article-publishing' },
  { route: '/growth/tenant-config' },
  { route: '/growth/search/geo/profiles' },
  { route: '/growth/comms/distributions' },
  { route: '/growth/comms/distributions/:runId', isParameterized: true },

  { route: '/comms/control-center' },
  { route: '/comms/telephony' },
  { route: '/comms/numbers' },
  { route: '/comms/carrier-profiles' },
  { route: '/comms/provisioning-orders' },
  { route: '/comms/provisioning-orders/:orderId', isParameterized: true },
  { route: '/comms/extensions' },
  { route: '/comms/agent-presence' },
  { route: '/comms/queues' },
  { route: '/comms/queues/:queueId', isParameterized: true },
  { route: '/comms/routing-rules' },
  { route: '/comms/ivr-flows' },
  { route: '/comms/ivr-flows/:ivrFlowId', isParameterized: true },
  { route: '/comms/interactions' },
  { route: '/comms/interactions/:interactionId', isParameterized: true },
  { route: '/comms/usage-billing' },
  { route: '/comms/voice-sessions/:voiceSessionId', isParameterized: true },
  { route: '/comms/voice-sessions/by-interaction/:interactionId', isParameterized: true },
  { route: '/comms/recovery-cases/:recoveryCaseId', isParameterized: true },
  { route: '/comms/recovery-cases/by-interaction/:interactionId', isParameterized: true },
  { route: '/comms/qa' },
  { route: '/comms/analytics' },
  { route: '/comms/agent-workspace' },
  { route: '/call-center', excludeFromMenuCheck: true },
  { route: '/call-center/queues', excludeFromMenuCheck: true },
  { route: '/call-center/interactions', excludeFromMenuCheck: true },
  { route: '/call-center/callbacks', excludeFromMenuCheck: true },

  { route: '/notifications/messages' },
  { route: '/notifications/messages/:messageId', isParameterized: true },
  { route: '/notifications/templates' },
  { route: '/notifications/templates/:templateId', isParameterized: true },
  { route: '/notifications/providers' },
  { route: '/notifications/delivery' },
  { route: '/notifications/policy' },
  { route: '/notifications/channels' },

  // Retired experimental /financial/* where launch-track equivalent exists (Phase 11.7).
  ...RETIRED_LEGACY_EXPERIMENTAL_APP_ROUTES,

  ...buildRouteRegistryEntries(PLACEHOLDER_SCREEN_DEFS)
];
